import React, { useRef, useState } from "react";
import { Box, ClickAwayListener, Divider, Popper, Typography, useTheme } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { StyledFilterPaper } from "@common/components/filterPopover/FilterPopover";
import { useTranslation } from "@localization/useTranslation";
import { FieldCitation } from "../addContractButton/createContractPane/CreateContractPane.types.ts";

export interface ContractAiUpdateCitationProps {
    citation: FieldCitation | undefined;
    onShowInDocument: (quote: string) => void;
}

const hoverCloseDelayInMilliseconds = 120;

/**
 * Where the AI read a value. The wrapper reveals it when the pointer is on the field; hovering the link
 * itself shows the passage, in the same paper the chat's citations use.
 *
 * A Popper rather than a tooltip because it flips and is portalled, so a citation on the last row of the
 * scroll is not clipped by the pane the way a downward tooltip was.
 */
function ContractAiUpdateCitation({ citation, onShowInDocument }: ContractAiUpdateCitationProps) {
    const theme = useTheme();
    const { translateString } = useTranslation();
    const anchorRef = useRef<HTMLSpanElement | null>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    if (!citation || (!citation.quote && !citation.section)) {
        return null;
    }

    const isLocated = citation.quoteLocated && !!citation.quote;

    function cancelScheduledClose() {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }

    function scheduleClose() {
        cancelScheduledClose();
        closeTimerRef.current = setTimeout(() => setIsPopoverOpen(false), hoverCloseDelayInMilliseconds);
    }

    function openNow() {
        cancelScheduledClose();
        setIsPopoverOpen(true);
    }

    function handleClick(event: React.MouseEvent) {
        event.preventDefault();
        event.stopPropagation();

        if (!isLocated) {
            return;
        }

        setIsPopoverOpen(false);
        onShowInDocument(citation!.quote!);
    }

    const heading = [citation.section, citation.pageNumber ? `${translateString("page")} ${citation.pageNumber}` : undefined].filter(Boolean).join(" · ");

    return (
        <>
            <Box
                ref={anchorRef}
                component="span"
                onClick={handleClick}
                onMouseEnter={openNow}
                onMouseLeave={scheduleClose}
                onFocus={openNow}
                onBlur={scheduleClose}
                tabIndex={0}
                sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: theme.typography.pxToRem(12),
                    color: isLocated ? theme.palette.primary.main : theme.palette.text.secondary,
                    cursor: isLocated ? "pointer" : "default",
                    lineHeight: 1.2,
                    "&:hover": isLocated ? { textDecoration: "underline" } : undefined,
                }}
            >
                <FormatQuoteIcon sx={{ fontSize: 14, transform: "scaleX(-1)" }} />
                {translateString("seeInDocument")}
            </Box>

            {isPopoverOpen && anchorRef.current && (
                <Popper
                    open
                    anchorEl={anchorRef.current}
                    placement="top-start"
                    style={{ zIndex: theme.zIndex.tooltip }}
                    modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
                >
                    <ClickAwayListener onClickAway={() => setIsPopoverOpen(false)}>
                        <StyledFilterPaper
                            onMouseEnter={openNow}
                            onMouseLeave={scheduleClose}
                            sx={{ width: 380, maxWidth: "90vw", padding: theme.spacing(1.5, 2), minWidth: 0 }}
                        >
                            {heading && <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.palette.text.primary }}>{heading}</Typography>}

                            {heading && <Divider sx={{ my: 1.5 }} />}

                            <Box sx={{ display: "flex", gap: 1.25, alignItems: "flex-start", maxHeight: 260, overflow: "auto" }}>
                                <FormatQuoteIcon sx={{ fontSize: 26, color: theme.palette.grey[400], transform: "scaleX(-1)", flexShrink: 0 }} />
                                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.55, color: theme.palette.text.primary }}>
                                    {citation.quote}
                                </Typography>
                            </Box>

                            {!isLocated && (
                                <Typography sx={{ marginTop: 1, fontSize: 11.5, color: theme.palette.text.secondary }}>
                                    {translateString("clauseNotFoundInDocument")}
                                </Typography>
                            )}
                        </StyledFilterPaper>
                    </ClickAwayListener>
                </Popper>
            )}
        </>
    );
}

export default ContractAiUpdateCitation;
